import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { UserDataService } from '../../../shared/services/userData.service';
import { FunctionService } from '../../../shared/services/function.service';
import { FormsModule } from '@angular/forms';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { CoursesOfActionService } from '../../../shared/services/coursesOfAction.service';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { IUserInfo } from '../../../shared/models/contact';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'functions-courses-of-action',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    FormsModule,
    EopAssistantComponent,
    IntroductionComponent,
    PrvNextButtonsComponent,
    PopupMessageComponent,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './functions-courses-of-action.component.html',
  styleUrl: './functions-courses-of-action.component.css'
})
export class FunctionsCoursesOfActionComponent implements OnInit {
  sectionTypeId = ESectionTypes["Function - Courses of Action"];
  subSectionTypeId = null;

  loading: boolean = false;

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  functionsList: any = [];
  selectedFunction: any | null = null;
  latestUpdate: any = {}; // Latest update for the building

  allCourseOfAction: any[] | null = null;
  selectedFunctionCoursesOfAction: any[] = [];
  initialFunctionCourseOfActionForm = {
    before_description: '',
    during_description: '',
    after_description: ''
  };  
  functionCourseOfActionForm = {
    before_description: '',
    during_description: '',
    after_description: ''
  };

  constructor(
    private userDataService: UserDataService,
    private functionService: FunctionService,
    private coursesOfActionService: CoursesOfActionService,
    public eopNavigationService: EopNavigationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadFunctions();
  }
  
  // load list of building's functions on the left column
  loadFunctions(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.functionService.getFunctions(this.currentBuildingId).subscribe({
        next: (response: any) => {
          this.loading = false;
          console.log(response);
          this.functionsList = response.data ? response.data : [];
          // Sort the list after retrieving functions list.
          if (this.functionsList.length > 0) {
            this.functionsList.sort((a: any, b: any) => a.function_name.localeCompare(b.function_name));
            // assign initial value for the function
            if (this.selectedFunction === null) {
              this.selectedFunction = this.functionsList[0] ? this.functionsList[0] : null;
            }
          }
          this.loadCourseOfAction();
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading functions:', error.message);
        }
      })
    }
  }

  // load building's all courses of action (of all functions)
  loadCourseOfAction(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.coursesOfActionService.getCourseOfAction(this.currentBuildingId, 'function').subscribe({
        next: (response: any) => {
          this.loading = false;
          console.log('Successfully retrieving Course of Action: ', response);
          this.allCourseOfAction = response.data ? response.data : null;
          if (this.allCourseOfAction) {
            this.latestUpdate = this.getLatestUpdate(this.allCourseOfAction);
            console.log('Latest update: ', this.latestUpdate);
            this.filterCourseOfAction();

            if (this.selectedFunctionCoursesOfAction.length > 0) {
              const formValues = {
                before_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.before_description || '',
                during_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.during_description || '',
                after_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.after_description || ''
              };
              
              this.functionCourseOfActionForm = { ...formValues };
              this.initialFunctionCourseOfActionForm = { ...formValues };
            }
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading course of action:', error.message);
        }
      })
    }
  }

  // get latest update for the building
  getLatestUpdate(objectList: any[]): { last_updated_at: string; last_updated_user_name: string } | null {
    if (!objectList || objectList.length === 0) return null;
  
    const latest = objectList.reduce((latest, current) => {
      const latestDate = new Date(latest.courses_of_action?.last_updated_at || 0);
      const currentDate = new Date(current.courses_of_action?.last_updated_at || 0);
      return currentDate > latestDate ? current : latest;
    });
  
    return {
      last_updated_at: latest.courses_of_action?.last_updated_at || '',
      last_updated_user_name: latest.courses_of_action?.last_updated_user_name || ''
    };
  }

  // Filter courses of action based on the selected function
  // default selected function is the first one in the function list above
  filterCourseOfAction(): void {
    if (this.allCourseOfAction && this.selectedFunction) {
      const functionId = Number(this.selectedFunction.id);
      this.selectedFunctionCoursesOfAction = this.allCourseOfAction
        .filter(item => Number(item.id) === functionId);
    }
  }

  hasFormChanged(): boolean {
    return (
      this.functionCourseOfActionForm.before_description !== this.initialFunctionCourseOfActionForm.before_description ||
      this.functionCourseOfActionForm.during_description !== this.initialFunctionCourseOfActionForm.during_description ||
      this.functionCourseOfActionForm.after_description !== this.initialFunctionCourseOfActionForm.after_description
    );
  } 

  // Set the selected function and filter the courses of action accordingly
  // This function is called when a function is selected from the left column
  setFunction(selectedItem: any): void {
    this.selectedFunction = selectedItem;
    this.filterCourseOfAction();

    this.functionCourseOfActionForm = {
      before_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.before_description || '',
      during_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.during_description || '',
      after_description: this.selectedFunctionCoursesOfAction[0]?.courses_of_action?.after_description || ''
    };
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.selectedFunction) {
      this.loading = true;
      console.log('Submitting:', this.functionCourseOfActionForm);
      this.coursesOfActionService.saveCourseOfAction(this.currentBuildingId, 'function', this.selectedFunction.id, this.functionCourseOfActionForm).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating courses of action for a function.';
            this.loadCourseOfAction(); // reload to get the latest data
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
          console.error('Error saving course of action:', error);
        }
      })
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
  }
}
